import React, { useState, useMemo } from 'react';
import { Download, Weight, Settings2, Info, Calendar, Save } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from '../hooks/use-toast';
import { saveQuote } from '../lib/quotes';

const CABLE_DATA = {
  'SuperSting Active (Dual Mode)': {
    weightFactor: 0.427,
    description: 'SuperSting Active Cable - Dual Mode'
  }
};

const DualModeCableCalculator = ({ activeYear, pricing }) => {
  const [cableType] = useState('SuperSting Active (Dual Mode)');
  const [interval, setInterval] = useState(3.0);
  const [numElectrodes, setNumElectrodes] = useState(28);
  const [leadIn, setLeadIn] = useState(2.0);
  const [tail, setTail] = useState(2.0);
  const [numConnectors, setNumConnectors] = useState(2);

  // Derive prices from Settings props
  const activeCablePrice = pricing.activeCablePrice;
  const connectorPrice = pricing.dualConnectorPrice;
  const electrodePrice = pricing.dualElectrodePrice;
  const overheadMultiplier = pricing.dualOverheadMultiplier;

  const M_TO_FT = 3.28084;
  const LB_TO_KG = 0.453592;

  // Calculate pricing using EXACT Excel formula for Dual Mode
  // Formula: =ROUNDUP((D10*O5 + D9*M7 + D6*M6) * 1.13, 0)
  // Note: NO profit multiplier (1.3) for Dual Mode!
  const results = useMemo(() => {
    const cable = CABLE_DATA[cableType];

    // Calculate total length (D10) from interval, electrodes, lead-in, and tail
    // D10 = interval × (electrodes - 1) + lead-in + tail
    const totalLengthMeters = interval * (numElectrodes - 1) + leadIn + tail;
    const totalLengthFeet = totalLengthMeters * M_TO_FT;

    // Apply EXACT Excel formula components
    // Part 1: D10 * O5 (total length × active cable price per meter)
    const cableCost = totalLengthMeters * activeCablePrice;

    // Part 2: D9 * M7 (num connectors × connector price)
    const connectorCost = numConnectors * connectorPrice;

    // Part 3: D6 * M6 (num electrodes × electrode price)
    const electrodeCost = numElectrodes * electrodePrice;

    // Calculate subtotal before markup
    const subtotalBeforeMarkup = cableCost + connectorCost + electrodeCost;

    // Apply overhead multiplier (1.13 = 13% overhead)
    const subtotalWithOverhead = subtotalBeforeMarkup * overheadMultiplier;

    // Apply ROUNDUP only (NO profit multiplier for Dual Mode)
    // In JavaScript, Math.ceil() is equivalent to ROUNDUP(..., 0)
    const totalPrice = Math.ceil(subtotalWithOverhead);

    // Calculate weight
    const weightLbs = (totalLengthMeters * cable.weightFactor) +
                      (numElectrodes * 1.03 / 16) +
                      (numConnectors * 4.5 / 16);
    const weightKg = weightLbs * LB_TO_KG;

    return {
      totalLengthMeters,
      totalLengthFeet,
      cableCost,
      connectorCost,
      electrodeCost,
      subtotalBeforeMarkup,
      overheadAmount: subtotalWithOverhead - subtotalBeforeMarkup,
      subtotalWithOverhead,
      totalPrice,
      weightLbs,
      weightKg
    };
  }, [cableType, interval, numElectrodes, leadIn, tail, numConnectors,
      activeCablePrice, connectorPrice, electrodePrice, overheadMultiplier]);

  const exportJSON = () => {
    const exportData = {
      configuration: {
        cableType,
        numElectrodes,
        totalLength: results.totalLengthMeters.toFixed(2) + ' m',
        numConnectors
      },
      pricing: {
        cableCost: results.cableCost.toFixed(2),
        connectorCost: results.connectorCost.toFixed(2),
        electrodeCost: results.electrodeCost.toFixed(2),
        subtotalBeforeMarkup: results.subtotalBeforeMarkup.toFixed(2),
        overheadAmount: results.overheadAmount.toFixed(2),
        subtotalWithOverhead: results.subtotalWithOverhead.toFixed(2),
        totalPrice: results.totalPrice.toFixed(2),
        currency: 'USD'
      },
      weight: {
        pounds: results.weightLbs.toFixed(2),
        kilograms: results.weightKg.toFixed(2)
      },
      formula: 'ROUNDUP((TotalLength*CablePrice + NumConnectors*(ConnectorFemale+ConnectorMale)/2 + NumElectrodes*ElectrodePrice) * 1.13, 0) * 1.3',
      timestamp: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agi-cable-quote-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Success', description: 'Price quote exported as JSON' });
  };

  const exportCSV = () => {
    const csvContent = [
      'AGI Cable Price Quote',
      `Generated: ${new Date().toLocaleString()}`,
      '',
      'Configuration',
      `Cable Type,${cableType}`,
      `Number of Electrodes,${numElectrodes}`,
      `Total Length,${results.totalLengthMeters.toFixed(2)} m (${results.totalLengthFeet.toFixed(2)} ft)`,
      `Connectors,${numConnectors}`,
      '',
      'Pricing Breakdown',
      `Cable Cost,$${results.cableCost.toFixed(2)}`,
      `Connector Cost,$${results.connectorCost.toFixed(2)}`,
      `Electrode Cost,$${results.electrodeCost.toFixed(2)}`,
      `Subtotal Before Markup,$${results.subtotalBeforeMarkup.toFixed(2)}`,
      `Overhead (${((overheadMultiplier - 1) * 100).toFixed(0)}%),$${results.overheadAmount.toFixed(2)}`,
      `Subtotal With Overhead,$${results.subtotalWithOverhead.toFixed(2)}`,
      `TOTAL PRICE,$${results.totalPrice.toFixed(2)}`,
      '',
      'Weight',
      `Total Weight,${results.weightLbs.toFixed(2)} lbs (${results.weightKg.toFixed(2)} kg)`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agi-cable-quote-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Success', description: 'Price quote exported as CSV' });
  };

  const exportPDF = () => {
    const pdfHTML = `
<!DOCTYPE html>
<html>
<head>
  <title>AGI Cable Price Quote</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 40px; color: #333; }
    .header { background: linear-gradient(135deg, #1e3a8a 0%, #10b981 100%); color: white; padding: 30px; border-radius: 10px; text-align: center; }
    .header h1 { margin: 0; font-size: 28px; }
    .section { margin: 30px 0; }
    .section h2 { color: #1e3a8a; border-bottom: 3px solid #10b981; padding-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; margin: 15px 0; }
    th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
    th { background: #1e3a8a; color: white; }
    .total-row { background: #dbeafe; font-weight: bold; font-size: 18px; }
    .info { background: #f0f9ff; padding: 15px; border-left: 4px solid #1e3a8a; margin: 20px 0; }
  </style>
</head>
<body>
  <div class="header">
    <h1>AGI Cable Price Quote</h1>
    <p>Advanced Geosciences Inc.</p>
    <p>${new Date().toLocaleDateString()}</p>
  </div>

  <div class="section">
    <h2>Cable Configuration</h2>
    <table>
      <tr><td><strong>Cable Type:</strong></td><td>${cableType}</td></tr>
      <tr><td><strong>Number of Electrodes:</strong></td><td>${numElectrodes}</td></tr>
      <tr><td><strong>Total Cable Length:</strong></td><td>${results.totalLengthMeters.toFixed(2)} m (${results.totalLengthFeet.toFixed(2)} ft)</td></tr>
      <tr><td><strong>Number of Connectors:</strong></td><td>${numConnectors}</td></tr>
    </table>
  </div>

  <div class="section">
    <h2>Price Breakdown</h2>
    <table>
      <thead>
        <tr><th>Item</th><th>Amount</th></tr>
      </thead>
      <tbody>
        <tr><td>Cable Cost</td><td>$${results.cableCost.toFixed(2)}</td></tr>
        <tr><td>Connector Cost</td><td>$${results.connectorCost.toFixed(2)}</td></tr>
        <tr><td>Electrode Cost</td><td>$${results.electrodeCost.toFixed(2)}</td></tr>
        <tr><td>Subtotal Before Markup</td><td>$${results.subtotalBeforeMarkup.toFixed(2)}</td></tr>
        <tr><td>Overhead (${((overheadMultiplier - 1) * 100).toFixed(0)}%)</td><td>$${results.overheadAmount.toFixed(2)}</td></tr>
        <tr><td>Subtotal With Overhead</td><td>$${results.subtotalWithOverhead.toFixed(2)}</td></tr>
        <tr class="total-row"><td><strong>TOTAL PRICE</strong></td><td><strong>$${results.totalPrice.toFixed(2)}</strong></td></tr>
      </tbody>
    </table>
  </div>

  <div class="section">
    <h2>Weight Information</h2>
    <table>
      <tr><td><strong>Total Weight:</strong></td><td>${results.weightLbs.toFixed(2)} lbs (${results.weightKg.toFixed(2)} kg)</td></tr>
    </table>
  </div>

  <div class="info">
    <strong>Pricing Formula:</strong> ROUNDUP((TotalLength × ActiveCablePrice + NumConnectors × ConnectorPrice + NumElectrodes × ElectrodePrice) × ${overheadMultiplier}, 0)
  </div>

  <p style="text-align: center; color: #64748b; margin-top: 40px;">All prices are in USD and based on AGI Cable Prices July 2025</p>
</body>
</html>`;

    const blob = new Blob([pdfHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agi-cable-quote-${Date.now()}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Success', description: 'Price quote exported as PDF (HTML format)' });
  };

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
        <div className="flex items-start justify-between">
          <div className="flex items-start">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
            <div>
              <p className="text-sm text-blue-900">
                <strong>Pricing Formula:</strong> Exact formula from AGI Cable Prices Excel:
              </p>
              <p className="text-xs font-mono text-blue-800 mt-1">
                ROUNDUP((TotalLength×ActiveCablePrice + Connectors×ConnectorPrice + Electrodes×ElectrodePrice) × {overheadMultiplier}, 0)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-lg ml-4 flex-shrink-0">
            <Calendar className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-700">{activeYear} Prices</span>
          </div>
        </div>
      </div>

      {/* Configuration Card */}
      <Card className="border-slate-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-emerald-50 border-b">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Settings2 className="w-5 h-5 text-[#1e3a8a]" />
            Cable Configuration
          </CardTitle>
          <CardDescription>Configure your SuperSting Active Cable (Dual Mode)</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Cable Type */}
            <div className="space-y-2">
              <Label htmlFor="cableType" className="text-slate-700 font-medium">Cable Type</Label>
              <Select value={cableType} disabled>
                <SelectTrigger id="cableType" className="border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(CABLE_DATA).map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">{CABLE_DATA[cableType].description}</p>
            </div>

            {/* Interval / Spacing */}
            <div className="space-y-2">
              <Label htmlFor="interval" className="text-slate-700 font-medium">
                Interval / Spacing (m)
              </Label>
              <Input
                id="interval"
                type="number"
                step="0.25"
                value={interval}
                onChange={(e) => setInterval(Number(e.target.value))}
                className="border-slate-300"
                min="0.1"
              />
            </div>

            {/* Number of Electrodes */}
            <div className="space-y-2">
              <Label htmlFor="numElectrodes" className="text-slate-700 font-medium">Number of Electrodes</Label>
              <Input
                id="numElectrodes"
                type="number"
                value={numElectrodes}
                onChange={(e) => setNumElectrodes(Number(e.target.value))}
                className="border-slate-300"
                min="1"
              />
              <p className="text-xs text-slate-500">Excel D6</p>
            </div>

            {/* Lead-in */}
            <div className="space-y-2">
              <Label htmlFor="leadIn" className="text-slate-700 font-medium">Lead-in (m)</Label>
              <Input
                id="leadIn"
                type="number"
                step="0.5"
                value={leadIn}
                onChange={(e) => setLeadIn(Number(e.target.value))}
                className="border-slate-300"
                min="0"
              />
            </div>

            {/* Tail */}
            <div className="space-y-2">
              <Label htmlFor="tail" className="text-slate-700 font-medium">Tail (m)</Label>
              <Input
                id="tail"
                type="number"
                step="0.5"
                value={tail}
                onChange={(e) => setTail(Number(e.target.value))}
                className="border-slate-300"
                min="0"
              />
            </div>

            {/* Number of Connectors */}
            <div className="space-y-2">
              <Label htmlFor="numConnectors" className="text-slate-700 font-medium">Number of Connectors</Label>
              <Input
                id="numConnectors"
                type="number"
                value={numConnectors}
                onChange={(e) => setNumConnectors(Number(e.target.value))}
                className="border-slate-300"
                min="0"
              />
              <p className="text-xs text-slate-500">Excel D9</p>
            </div>
          </div>

          {/* Total Length and Price */}
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="text-sm text-slate-600 mb-1">Total Cable Length (calculated):</div>
              <div className="text-2xl font-bold text-[#1e3a8a]">
                {results.totalLengthMeters.toFixed(2)} m ({results.totalLengthFeet.toFixed(2)} ft)
              </div>
              <div className="text-xs text-slate-500 mt-1">Excel D10: Interval × (Electrodes-1) + Lead-in + Tail</div>
            </div>

            <div className="p-4 bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg border-2 border-green-300">
              <div className="text-sm text-green-700 mb-1 font-semibold">TOTAL PRICE (ROUNDUP Applied):</div>
              <div className="text-2xl font-bold text-green-700">
                ${results.totalPrice.toFixed(2)}
              </div>
              <div className="text-xs text-green-600 mt-1">Using {activeYear} pricing</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Weight Results */}
      <Card className="border-blue-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-blue-200">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Weight className="w-5 h-5 text-blue-600" />
            Weight Calculation (Excel Formula D28)
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="text-sm text-slate-600 mb-1">Total Weight</div>
              <div className="text-3xl font-bold text-[#1e3a8a]">{results.weightLbs.toFixed(2)}</div>
              <div className="text-sm text-slate-500 mt-1">pounds (lbs)</div>
            </div>

            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="text-sm text-slate-600 mb-1">Total Weight</div>
              <div className="text-3xl font-bold text-[#1e3a8a]">{results.weightKg.toFixed(2)}</div>
              <div className="text-sm text-slate-500 mt-1">kilograms (kg)</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Buttons */}
      <Card className="border-slate-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Download className="w-5 h-5 text-slate-700" />
            Export Quote
          </CardTitle>
          <CardDescription>Download your price quote in various formats</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={exportJSON}
              className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] hover:from-[#1e40af] hover:to-[#10b981] text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Export JSON
            </Button>
            <Button
              onClick={exportCSV}
              className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] hover:from-[#1e40af] hover:to-[#10b981] text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={exportPDF}
              className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] hover:from-[#1e40af] hover:to-[#10b981] text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button
              disabled
              title="Coming soon — requires database setup"
              className="bg-gradient-to-r from-emerald-600 to-green-600 text-white opacity-50 cursor-not-allowed"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Quote
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DualModeCableCalculator;